import { Wrapper } from 'components/ArticleComponents'
import { Provider as StepProvider } from 'components/ArticleStep'
import { Renderer } from 'components/CustomMDX'
import { graphql } from 'gatsby'
import useDocument from 'hooks/useDocument'
import useDocumentsPagination from 'hooks/useDocumentsPagination'
import { omitBy } from 'lodash'
import React, { useEffect } from 'react'
import getMeta from 'utils/getMeta'
import getParentLocation from 'utils/getParentLocation'
import { withPrefix } from 'utils/prefix'
import { useLayoutContext } from '../components/LayoutContext'
import Flex from 'ds/Flex'
import TitleIcons from 'components/TitleIcons'
import CamelCaseWrap from 'components/CamelCaseWrap'

export default function DocumentTemplate({ children, ...props }) {
	// when MDX files are statically imported, they don't have `data`, they have `children` instead.
	// this is what enables partial/nested MDX
	if (children) return children

	// `data` and `location` are only available in MDX files when queried via GraphQL
	return <QueriedMdxTemplate {...props} />
}

function QueriedMdxTemplate({ data, location }) {
	const { setLayoutProps } = useLayoutContext()

	const { frontmatter, body, fields } = data?.mdx || {}
	const { pagination, customPagination, fullWidth, hideTopSection, icon } = frontmatter || {}

	const parentLocation = getParentLocation(location)

	const parentPathname = fields?.modSlug?.replace(/\/[^\/]*$/, '') || ''
	const parentDocument = useDocument(withPrefix(parentPathname)) || {}

	const isSection = !!frontmatter.switcherData
	const adjustedFrontMatter = isSection
		? {
				...(parentDocument?.frontmatter || {}),
				...omitBy(frontmatter, (value, key) => value === null || (key === 'title' && !value)),
		  }
		: frontmatter

	const { title } = adjustedFrontMatter

	const meta = getMeta(adjustedFrontMatter)

	const documentsPagination = useDocumentsPagination({ slug: fields.modSlug })

	useEffect(() => {
		setLayoutProps({
			seoProps: meta,
			showBreadcrumbs: true,
			topSectionProps: {
				...adjustedFrontMatter,
				title: (
					<Flex justify='flex-start' align='baseline'>
						{icon?.publicURL && <TitleIcons icons={[{ src: icon.publicURL, name: title }]} />}
						<CamelCaseWrap text={title} />
					</Flex>
				),
			},
			hideTopSection,
			hasPagination: pagination,
			pagination: customPagination ?? documentsPagination,
			fullWidth,
			modSlug: fields.modSlug,
			tableOfContentsMaxDepth: adjustedFrontMatter.tableOfContents?.maxDepth,
		})
	}, [])

	const hasSection = isSection && parentDocument.body

	return (
		<StepProvider>
			<Wrapper>
				{/* Parent */}
				{hasSection ? (
					<Renderer
						parentLocation={parentLocation}
						location={location}
						contentSwitcherContent={
							<Wrapper>
								<Renderer parentLocation={parentLocation} location={location}>
									{body}
								</Renderer>
							</Wrapper>
						}
					>
						{parentDocument.body}
					</Renderer>
				) : (
					<Renderer parentLocation={parentLocation} location={location}>
						{body}
					</Renderer>
				)}
			</Wrapper>
		</StepProvider>
	)
}

export const query = graphql`
	query ($id: String!) {
		mdx(fields: { id: { eq: $id } }) {
			body
			fields {
				id
				slug
				modSlug
			}
			parent {
				... on File {
					id
					name
					relativePath
				}
			}
			tableOfContents(maxDepth: 6)
			frontmatter {
				title
				documentTitle
				heading
				tags
				description
				metaTitle
				metaDescription
				virtual
				fullWidth
				hideTopSection
				icon {
					publicURL
				}
				switcherData {
					uses
					id
					label
				}
				switcherInit {
					uses
					default
				}
				customPagination {
					prev {
						url
						title
					}
					next {
						url
						title
					}
				}
				nested
				pagination
				siblings
				tableOfContents {
					maxDepth
				}
			}
			headings {
				value
				depth
			}
		}
	}
`
